import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Chip,
  IconButton,
  Modal,
  Typography
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { DATE_TIME_FORMAT_SHORT, RefactoredDevice, show_date } from '../../../@types/model/device';
import { useTranslation } from 'react-i18next';
import PercentDeliver from './pieces/PercentDeliver';
import DeliveryBar from './DeliveryBar';
import RowDeliver from './pieces/RowDeliver';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { DeliverFilter } from '../../../@types/model/deliver';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { ProductSchedule } from '../../../@types/model/productSchedule';
import ScheduleBar from './ScheduleBar';
import SvgIconStyle from 'components/SvgIconStyle';
import { DeliveryRequestItem } from '../../../@types/model/deliveryRequest';
import getDisabledRemove from './functions/getDisabledRemove';
import getDisabledAdd from './functions/getDisabledAdd';
import { DeliveryConfiguration } from '../../../@types/model/deliveryConfiguration';
import { buildingState } from 'recoil/buildingState';
import { Building } from '../../../@types/model/user';
import { useRecoilValue } from 'recoil';
import BuildingTag from 'components/building/BuildingTag';

const ButtonGroupDeliver = styled(ButtonGroup)(({ theme }) => ({
  marginLeft: 20,
  width: '45%',
  display: 'inline-block',
  [theme.breakpoints.up('sm')]: {
    width: 150,
    display: 'inherit'
  },
  textAlign: 'right',
  justifyContent: 'center'
}));

type NewDeliverRowProps = {
  rd: RefactoredDevice;
  slot_amount: number;
  unit: string;
  tableFilter: DeliverFilter;
  totalTruck: number;
  max: number;
  addSlot: Function;
  removeSlot: Function;
  refactoredDevices: RefactoredDevice[];
  productSchedules: ProductSchedule[];
  deliveryRequestItems: DeliveryRequestItem[];
  deliveryConfiguration: DeliveryConfiguration | null;
};
const NewDeliverSilosRow = (props: NewDeliverRowProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const addSlot = (guid: string) => {
    props.addSlot(guid);
  };

  const removeSlot = (guid: string) => {
    props.removeSlot(guid);
  };

  const [open, setOpen] = useState(false);

  const [devicePendingDelivery, setDevicePendingDelivery] = useState<boolean>(false);

  const [disabledRemove, setDisabledRemove] = useState(true);
  const [disabledAdd, setDisabledAdd] = useState(true);
  const [extraPercentage, setExtraPercentage] = useState(5);
  const [percentInitial, setPercentInitial] = useState(100);
  const [percentSlots, setPercentSlots] = useState(100);
  const [totalFilledKg, setTotalFilledKg] = useState(0);
  const [building, setBuilding] = useState<Building | undefined>(undefined);

  const buildings: Building[] = useRecoilValue(buildingState);

  useEffect(() => {
    initComponent();
  }, [props]);

  function initComponent() {
    const diff = props.tableFilter.receivedDate?.diff(dayjs(), 'day') ?? 2;

    let extra_percentage = diff * 5;
    let percent_initial = props.rd.percent;
    let percent_slots = (props.slot_amount / props.rd.capacidad) * props.rd.slotsNumber * 100;

    percent_initial = percent_initial / (1 + extra_percentage / 100);
    percent_slots = percent_slots / (1 + extra_percentage / 100);

    let totalFilledKg = 0;
    props.deliveryRequestItems
      .filter((sri: DeliveryRequestItem) => {
        let deviceGuid = props.rd.guid;
        if (props.rd.attributes.is_ref_of && props.rd.attributes.is_ref_of.split(':').length === 3)
          deviceGuid = props.rd.attributes.is_ref_of.split(':')[2];
        return sri.device === deviceGuid;
      })
      .forEach((sri: DeliveryRequestItem) => (totalFilledKg += sri.quantity));

    percent_slots = ((totalFilledKg / props.rd.capacidad) * 100) / (1 + extra_percentage / 100);

    setTotalFilledKg(totalFilledKg);
    setExtraPercentage(extra_percentage);
    setPercentInitial(percent_initial);
    setPercentSlots(percent_slots);

    // Set if the button remove slot is disabled or enabled
    setDisabledRemove(getDisabledRemove(props.tableFilter, props.deliveryRequestItems, props.rd));

    // Set if the button add slot is disabled or enabled
    setDisabledAdd(
      getDisabledAdd(
        props.tableFilter,
        props.deliveryRequestItems,
        props.productSchedules,
        props.rd,
        props.deliveryConfiguration
      )
    );
    setBuilding(buildings.find((b: Building) => props.rd.attributes.tags.includes(b.guid)));
  }

  let pendingDeliveries =
    props.rd.delivery_request_pending && props.rd.delivery_request_pending.length;
  let showBar = true;
  if (pendingDeliveries) {
    if (
      props.rd.productSchedule &&
      props.rd.productSchedule.schedule &&
      props.rd.productSchedule.schedule.length > 0
    ) {
      if (props.rd.productSchedule.schedule[0].infinite) showBar = false;
    } else {
      showBar = false;
    }
  }

  return (
    <RowDeliver
      key={props.rd.guid}
      className="newDeliverSilorRow"
      style={showBar && pendingDeliveries ? { height: 130 } : {}}
    >
      <Typography
        variant="h6"
        component="h6"
        className="tituloCrianza"
        style={{
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: 'calc(100% - 200px)'
          }
        }}
      >
        <div className="titulo">
          {props.rd.attributes.name}
          <BuildingTag building={building} />
          <IconButton onClick={() => setOpen(true)}>
            <SvgIconStyle
              src={'/static/icons/giraf/magnifying-glass-regular.svg'}
              sx={{ width: '20px', height: '20px', display: 'block' }}
            />
          </IconButton>
          <Modal
            open={open}
            onClose={() => setOpen(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box className="modalDetailCrianza">
              <Typography id="modal-modal-title" variant="h6" component="h2">
                {props.rd.attributes.name}
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                <h3>{t('device_details')}</h3>
                <div className="mb-5">
                  <b>{t('identifier')}:</b> {props.rd.attributes.identifier}
                </div>
                <div className="mb-5">
                  <b>{t('device_type')}:</b> {props.rd.attributes.device_type_preset}
                </div>
                <br />
                <h3>{t('breeding_details')}</h3>
                <div className="mb-5">
                  <b>{t('Breeding')}:</b>{' '}
                  {props.rd.productSchedule?.product_schedule_template?.name}
                </div>
                <div className="mb-5">
                  <b>{t('breeding_number')}:</b> {props.rd.productSchedule?.reference}
                </div>
                <div className="mb-5">
                  <b>{t('units')}:</b> {props.rd.productSchedule?.configuration.units}
                </div>
                <div className="mb-5">
                  <b>{t('breeding_devices')}:</b>
                  {props.rd.productSchedule?.devices
                    .map((guid: string) => {
                      const rdFiltered = props.refactoredDevices.filter((rd: RefactoredDevice) => {
                        if (rd.guid === guid) return true;
                        return false;
                      });
                      if (rdFiltered.length > 0) return rdFiltered[0].attributes.name;
                      return '';
                    })
                    .map((device: string, index: number) => {
                      return (
                        <span className="device" key={index}>
                          {device}
                        </span>
                      );
                    })}
                </div>
                <div style={{ paddingBottom: 20 }}>
                  <b>
                    {t('phases')} {t('breeding')}
                  </b>
                  <br />
                  {!props.rd.productSchedule ? (
                    <div className="sinCrianza">
                      <Chip label={t('no_breeding')} color="error" size="small" />
                    </div>
                  ) : (
                    <div>
                      {showBar ? (
                        <ScheduleBar
                          schedule={props.rd.productSchedule}
                          slot_amount={props.slot_amount}
                          slotsNumber={props.rd.slotsNumber}
                          expand={true}
                          deliveryRequestItems={props.deliveryRequestItems}
                        ></ScheduleBar>
                      ) : null}
                      {pendingDeliveries ? (
                        <div className="sinCrianza">
                          <Chip
                            onClick={() => setDevicePendingDelivery(true)}
                            label={t('pending_deliveries')}
                            color="error"
                            size="small"
                            avatar={
                              <SvgIconStyle
                                src={'/static/icons/giraf/magnifying-glass-regular.svg'}
                                sx={{
                                  width: '20px',
                                  height: '20px',
                                  display: 'block',
                                  margin: '5px',
                                  marginRight: '5px'
                                }}
                              />
                            }
                          />
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </Typography>
            </Box>
          </Modal>
        </div>
        {!props.rd.productSchedule ? (
          <div className="sinCrianza">
            <Chip label="Sin crianza" color="error" size="small" />
          </div>
        ) : (
          <div>
            {/* {JSON.stringify(props.rd.productSchedule)} */}
            {showBar ? (
              <ScheduleBar
                schedule={props.rd.productSchedule}
                slot_amount={props.slot_amount}
                slotsNumber={props.rd.slotsNumber}
                expand={false}
                deliveryRequestItems={props.deliveryRequestItems}
              ></ScheduleBar>
            ) : null}
            {pendingDeliveries ? (
              <div className="sinCrianza">
                <Chip
                  onClick={() => setDevicePendingDelivery(true)}
                  label={t('pending_deliveries')}
                  color="error"
                  size="small"
                  avatar={
                    <SvgIconStyle
                      src={'/static/icons/giraf/magnifying-glass-regular.svg'}
                      sx={{
                        width: '20px',
                        height: '20px',
                        display: 'block',
                        margin: '5px',
                        marginRight: '5px'
                      }}
                    />
                  }
                />
              </div>
            ) : null}
          </div>
        )}
      </Typography>
      <PercentDeliver className="percentDeliver">
        <DeliveryBar
          rd={props.rd}
          unit={props.unit}
          tableFilter={props.tableFilter}
          percentSlots={percentSlots}
          percentInitial={percentInitial}
          extraPercentage={extraPercentage}
          amountSlots={totalFilledKg}
        />
        <ButtonGroupDeliver
          className="buttonGroupDeliver"
          variant="outlined"
          aria-label="outlined button group"
        >
          <Button
            onClick={() => removeSlot(props.rd.guid)}
            disabled={disabledRemove}
            style={{
              padding: 3,
              height: 40,
              background: 'white'
            }}
          >
            <RemoveIcon />
          </Button>
          <Button
            onClick={() => addSlot(props.rd.guid)}
            disabled={disabledAdd}
            style={{
              padding: 3,
              height: 40,
              background: 'white'
            }}
          >
            <AddIcon />
          </Button>
        </ButtonGroupDeliver>
      </PercentDeliver>
      <Modal
        open={devicePendingDelivery}
        onClose={() => setDevicePendingDelivery(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="modalDetailCrianza">
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {props.rd.attributes.name}
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 2 }}>
            {t('pending_delivery_requests')}
          </Typography>
          {props?.rd?.delivery_request_pending?.map((delivery_request_pending) => {
            return (
              <div
                key={delivery_request_pending.guid}
                style={{
                  borderColor: 'gray',
                  borderWidth: 0.5,
                  borderStyle: 'solid',
                  padding: 10,
                  marginBottom: 10
                }}
              >
                <div className="mb-5">
                  <h4>{t('delivery_request')}</h4>
                  <b>{t('created_date')}:</b>
                  {typeof delivery_request_pending.created_at !== 'undefined' && (
                    <span>
                      {' '}
                      {show_date(delivery_request_pending.created_at, DATE_TIME_FORMAT_SHORT)}
                    </span>
                  )}
                </div>
                <div className="mb-5">
                  <b>{t('delivery_date')}:</b>
                  {typeof delivery_request_pending.delivery_date !== 'undefined' && (
                    <span>
                      {' '}
                      {show_date(delivery_request_pending.delivery_date, DATE_TIME_FORMAT_SHORT)}
                    </span>
                  )}
                </div>
                <div className="mb-5">
                  <b>{t('items')}:</b>
                  {<span> {delivery_request_pending.items?.length}</span>}
                </div>
                <div className="mb-5">
                  <b>{t('total')}:</b>
                  <span>
                    {' '}
                    {delivery_request_pending.items
                      ?.map((item) => item.quantity)
                      .reduce((partialSum, a) => partialSum + a, 0)}
                    {' Kg'}
                  </span>
                </div>
              </div>
            );
          })}
        </Box>
      </Modal>
    </RowDeliver>
  );
};

export default NewDeliverSilosRow;
